import { ViewpointBuilderTab } from 'viewpointBuilder/viewpointBuilderNavigation/types';
import {
  InitialViewpointBuilderConfiguration,
  ViewpointBuilderCommands,
  ViewpointBuilderContext,
} from 'viewpointBuilder/types';
import {
  DirectedTripleWithFilters,
  FilterInterfaceFilter,
  FormattingFilter,
  QueryBuilderQuery,
  QueryBuilderSubquery,
  ComponentSelection,
  hasStartQuery,
  hasStartSet,
  FilterTypes,
} from '@ardoq/api-types';
import { isAdvancedSearchQuery } from './getComponentSelection';
import { filterOperations, DEFAULT_SOLUTION_METADATA } from '@ardoq/core';
import { isPerspectiveGroupingRule } from '@ardoq/perspectives';
import { v4 as uuidv4 } from 'uuid';
import { mapToPerspectiveEditorFormattingRule } from 'streams/perspectiveEditorData/mapToPerspectiveEditorFormattingRule';
import { isTruthy } from '@ardoq/common-helpers';
import { dispatchAction } from '@ardoq/rxbeach';
import { updateAdvancedSearch } from 'search/AdvancedSearch/actions';
import { ComponentSearchData } from './selectContextTab/types';
import { getStartSetFilter } from './getStartSetFilter';
import { setShouldIncludeInstanceCounts } from './traversals/editTraversalActions';
import { setFolderId } from './viewpointMetaInfo/metaInfoActions';
import { resetViewpointBuilder } from './resetViewpointBuilder';
import { ReferenceLabelSource } from '@ardoq/data-model';
import { getSupportedTraversalViewIdOrDefault } from 'views/environmentals/getViewpointModeSupportedViews';
import { getLabelFormattingFromFilterAttributes } from '@ardoq/filter-interface';
import { isDefaultWildcardTraversal } from 'traverseOneLevel/defaultWildcardViewpoint';
import { hasDefaultGreyColorFilter } from 'models/utils/filterUtils';
import { pathOperations } from 'traversals/pathOperations';

type InitViewpointBuilderArgs = {
  namespace: string;
  activeTab?: ViewpointBuilderTab;
  context: ViewpointBuilderContext;
  initialConfiguration?: InitialViewpointBuilderConfiguration;
  folderId?: string | null;
  viewpointBuilderCommands: ViewpointBuilderCommands;
};

export const initViewpointBuilder = ({
  namespace,
  activeTab,
  context,
  initialConfiguration,
  folderId,
  viewpointBuilderCommands,
}: InitViewpointBuilderArgs) => {
  const startSetFilter = initialConfiguration
    ? getStartSetFilter({
        paths: initialConfiguration.initialPaths,
        filters: initialConfiguration.filters,
      })
    : null;

  dispatchAction(
    setShouldIncludeInstanceCounts(shouldIncludeInstanceCounts(context)),
    namespace
  );

  resetViewpointBuilder(namespace);
  dispatchAction(setFolderId(folderId ?? null), namespace);

  viewpointBuilderCommands.navigationCommands.selectViewpointBuilderContext(
    context
  );
  if (!initialConfiguration) {
    if (activeTab) {
      viewpointBuilderCommands.navigationCommands.selectTab(activeTab);
    }
    return;
  }

  const {
    initialPaths,
    filters,
    name,
    viewName,
    description,
    startType: initialStartType,
    groupBys,
    conditionalFormatting,
    viewpointId,
    viewpointVersion,
    loadedStateHash,
    pathMatching,
    pathCollapsingRules,
    startSetExpansion,
    startSetExpansionFromType,
    isMainLoadedState,
    loadedComponents,
    solutionMetadata,
  } = initialConfiguration;

  const startType = getStartType(
    loadedComponents,
    initialPaths,
    initialStartType,
    startSetExpansionFromType
  );

  initSearchTab({
    ...initialConfiguration,
    startType: startType ?? undefined,
    commands: viewpointBuilderCommands,
    context,
    startSetFilter,
    namespace,
  });

  if (startSetExpansion) {
    viewpointBuilderCommands.editTraversalCommands.setStartSetExpansion(
      startSetExpansion
    );
  }

  if (initialPaths) {
    viewpointBuilderCommands.editTraversalCommands.setTraversals(
      initialPaths,
      filters
    );
  }

  if (pathMatching) {
    viewpointBuilderCommands.editTraversalCommands.setPathMatchingType(
      pathMatching
    );
  }

  if (pathCollapsingRules) {
    viewpointBuilderCommands.collapsePathsCommands.setPathCollapsingRules(
      pathCollapsingRules
    );
  }

  viewpointBuilderCommands.metaInfoCommands.setInitialMetaInfo({
    initialState: {
      name: name ?? null,
      description: description ?? null,
      viewName: getSupportedTraversalViewIdOrDefault(viewName),
      _id: viewpointId ?? null,
      _version: viewpointVersion ?? null,
      loadedStateHash: loadedStateHash ?? null,
      isMainLoadedState: isMainLoadedState ?? false,
      solutionMetadata: solutionMetadata ?? DEFAULT_SOLUTION_METADATA,
    },
  });

  if (groupBys) {
    viewpointBuilderCommands.groupingsCommands.setGroupingRules(
      groupBys
        .map(rule => ({ ...rule, id: uuidv4() }))
        .filter(isPerspectiveGroupingRule)
    );
  }

  if (conditionalFormatting) {
    applyFormatting(viewpointBuilderCommands, conditionalFormatting);
  }

  if (viewName && context === 'editViewpoint') {
    viewpointBuilderCommands.selectedViewCommands.setSelectedView({
      value: viewName,
      label: viewName,
    });
  }

  if (activeTab) {
    viewpointBuilderCommands.navigationCommands.selectTab(activeTab);
  }
};

const initBasicSearch = (
  componentSelection: ComponentSelection | null,
  loadedComponents: ComponentSearchData[] | undefined,
  commands: ViewpointBuilderCommands,
  startType: string | undefined,
  startSearchTerm: string | undefined,
  context: ViewpointBuilderContext,
  startSetFilter: QueryBuilderSubquery | null
) => {
  if (
    componentSelection &&
    hasStartSet(componentSelection) &&
    loadedComponents
  ) {
    commands.componentsSearchCommands.setLoadedComponents({
      loadedComponents,
      totalCount: loadedComponents.length,
    });
    commands.componentsSearchCommands.setSelectedComponents(
      loadedComponents.map(component => ({
        ...component,
        isSelected: true,
      }))
    );
  }

  const shouldApplyStartSetQuery = context === 'openViewpoint';
  if (startSetFilter && shouldApplyStartSetQuery) {
    commands.componentsSearchCommands.setStartSetFilter(startSetFilter);
  }

  if (startType) {
    commands.componentsSearchCommands.setComponentTypeName(startType);
    commands.editTraversalCommands.setStartTypeAndTraversalStartSetOrCount(
      startType,
      componentSelection && hasStartSet(componentSelection)
        ? componentSelection.startSet
        : []
    );
  }
  if (startSearchTerm) {
    commands.componentsSearchCommands.setComponentName(startSearchTerm);
  }
  if (componentSelection && hasStartQuery(componentSelection)) {
    commands.componentsSearchCommands.setTraversalStartQuery(
      componentSelection.startQuery
    );
    commands.componentsSearchCommands.setAllComponentsSelected(true);
  }
};

const initSearchTab = ({
  componentSelection,
  loadedComponents,
  startType,
  startSearchTerm,
  commands,
  context,
  startSetFilter,
  namespace,
}: InitialViewpointBuilderConfiguration & {
  commands: ViewpointBuilderCommands;
  context: ViewpointBuilderContext;
  startSetFilter: QueryBuilderSubquery | null;
  namespace: string;
}) => {
  if (
    componentSelection &&
    hasStartQuery(componentSelection) &&
    isAdvancedSearchQuery(componentSelection.startQuery)
  ) {
    initAdvancedSearch(
      componentSelection.startQuery,
      commands,
      namespace,
      startType
    );
  } else {
    initBasicSearch(
      componentSelection,
      loadedComponents,
      commands,
      startType,
      startSearchTerm,
      context,
      startSetFilter
    );
  }
};

const initAdvancedSearch = (
  startQuery: QueryBuilderQuery,
  commands: ViewpointBuilderCommands,
  namespace: string,
  startType?: string
) => {
  // We need to extract the user-added rules from the query,
  // rules[0] contains the SearchContextRule.
  const queryBuilderSubQuery = startQuery.rules[1];
  dispatchAction(
    updateAdvancedSearch({ queryBuilderRules: queryBuilderSubQuery }),
    namespace
  );
  commands.componentsSearchCommands.viewpointBuilderAdvancedSearchQueryUpdated({
    isValid: true,
    isEmpty: false,
    query: startQuery,
  });
  commands.componentsSearchCommands.setAllComponentsSelected(true);
  if (startType) {
    commands.editTraversalCommands.setStartType(startType);
  }
};

const applyFormatting = (
  viewpointBuilderCommands: ViewpointBuilderCommands,
  conditionalFormatting: (FormattingFilter | FilterInterfaceFilter)[]
) => {
  viewpointBuilderCommands.formattingCommands.setFormattingRules({
    formattingRules: conditionalFormatting
      .filter(filterOperations.isStrictlyFormattingFilter)
      .map(mapToPerspectiveEditorFormattingRule)
      .filter(isTruthy),
  });

  const labelFormatting = getLabelFormattingFromFilterAttributes(
    conditionalFormatting
  );
  viewpointBuilderCommands.formattingCommands.setLabelFormatting({
    labelFormatting,
  });

  const showReferenceType =
    !conditionalFormatting.length ||
    conditionalFormatting.some(
      ({ value, type }) =>
        type === FilterTypes.REFERENCE_LABEL &&
        (value === ReferenceLabelSource.REFERENCE_TYPE ||
          // DISPLAY_TEXT_OR_REFERENCE_TYPE is a legacy value which is not in use anymore
          value === ReferenceLabelSource.DISPLAY_TEXT_OR_REFERENCE_TYPE)
    );

  viewpointBuilderCommands.formattingCommands.onShowReferenceTypeToggled(
    showReferenceType
  );

  // Initialize "keep original color for non-formatted data" from the viewpoint data
  const hasDefaultGreyColor = hasDefaultGreyColorFilter(conditionalFormatting);
  const keepOriginalColor = !hasDefaultGreyColor;
  viewpointBuilderCommands.formattingCommands.setKeepOriginalColor(
    keepOriginalColor
  );

  return;
};

const contextWithInstanceCounts = new Set<ViewpointBuilderContext>([
  'editSubgraph',
  'contextMenu',
  'loadNewViewpoint',
]);

const shouldIncludeInstanceCounts = (context: ViewpointBuilderContext) => {
  return contextWithInstanceCounts.has(context);
};

const getStartType = (
  loadedComponents: ComponentSearchData[] | undefined,
  initialPaths: DirectedTripleWithFilters[][] | undefined,
  initialStartType: string | undefined,
  startSetExpansionFromType: string | undefined
) => {
  if (
    initialPaths &&
    loadedComponents &&
    loadedComponents.length > 0 &&
    isDefaultWildcardTraversal(initialPaths)
  ) {
    return loadedComponents[0].entityType;
  }

  // A hierarchy expansion stores `anyType` as the start endpoint of its paths.
  // The concrete type is carried alongside, and the hierarchy node is rebuilt
  // from it once the metamodel is applied.
  if (startSetExpansionFromType) {
    return startSetExpansionFromType;
  }

  const startTypeFromPaths = initialPaths
    ? pathOperations.getStartTypeFromPaths(initialPaths)
    : null;

  return startTypeFromPaths ?? initialStartType ?? null;
};
